import { useCallback, useEffect, useMemo, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { getEmptyAddress } from "@jti/commerce";
import type { AddressHomeDeliveryFieldValue } from "@jti/commerce-types";
import { toast } from "@jti/core-components/components/Toaster";
import { newrelic } from "@jti/fe-clients";
import type { Address, AddressStored } from "@jti/fe-common";
import { isAddressFormExtendedWithProvince } from "@jti/fe-common";
import { useGetCustomerCartIdQuery } from "@jti/store/slices/magentoApiSlice";
import { useSaveAddressMutation } from "@jti/store/slices/profileApiSlice";
import { useApplyAddressOverrides, useFormContext, useTranslations } from "@jti/ui";

import type { AddressHomeDeliveryFieldConfig } from "@checkout-v2/formFields/factories/addressHomeDeliveryFieldFactory";
import { addressHomeDeliveryFieldFactory } from "@checkout-v2/formFields/factories/addressHomeDeliveryFieldFactory";
import { hasAddressHomeDeliveryValue } from "@checkout-v2/formFields/factories/addressHomeDeliveryFieldFactory/utils";
import type { FormFieldValues } from "@checkout-v2/forms/forms.types";
import { useCountry } from "@checkout-v2/shared/hooks/useCountry";
import { useSetShippingAddressOnCart } from "@checkout-v2/shared/hooks/useSetShippingAddressOnCart";
import { getShippingAddressErrorMessage } from "@checkout-v2/shared/utils";
import { getMagentoAddress } from "@checkout-v2/shared/utils/getMagentoAddress";

export type AddressHomeDeliveryFieldMode = "view" | "new";

export type UseAddressHomeDeliveryProps = {
  fieldConfig: AddressHomeDeliveryFieldConfig;
  firstname: string;
  lastname: string;
  telephone: string;
};

export type UseAddressHomeDeliveryResult = {
  mode: AddressHomeDeliveryFieldMode;
  isFormMode: boolean;
  homeDeliveryAddress: AddressHomeDeliveryFieldValue;
  homeDeliveryAddressWithOverrides: AddressHomeDeliveryFieldValue;
  immutableProvinceCode?: string;
  shippingAddressErrorMessage?: string;
  isAddressDialogOpen: boolean;
  isSaveAddressPending: boolean;
  isOnDialogSubmitLoading: boolean;
  showShippingAddressSelector: () => void;
  hideShippingAddressSelector: () => void;
  showNewAddressForm: () => void;
  onAddressSelect: (address: AddressStored) => Promise<void>;
  validateNewAddress: () => Promise<boolean>;
  saveNewAddress: (addressOverride?: AddressStored) => Promise<boolean>;
};

export const useAddressHomeDelivery = ({
  fieldConfig,
  firstname,
  lastname,
  telephone,
}: UseAddressHomeDeliveryProps): UseAddressHomeDeliveryResult => {
  const { __ } = useTranslations();
  const config = useConfig();
  const { applyAddressOverrides } = useApplyAddressOverrides();
  const isAddressFormWithProvince = isAddressFormExtendedWithProvince(config.addressFormVariant);

  const form = useFormContext<FormFieldValues<{ [fieldConfig.name]: AddressHomeDeliveryFieldValue }>>();

  const [mode, setMode] = useState<AddressHomeDeliveryFieldMode>(() => {
    return fieldConfig.hasDefaultAddress || hasAddressHomeDeliveryValue(form.getValues(fieldConfig.name))
      ? "view"
      : "new";
  });

  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState<boolean>(false);

  const getCustomerCartIdQuery = useGetCustomerCartIdQuery();
  const { setShippingAddressOnCartState, setShippingAddressOnCart } = useSetShippingAddressOnCart();
  const [saveAddressInProfileMutation, saveAddressInProfileMutationResult] = useSaveAddressMutation();
  const countryQueryState = useCountry();

  const homeDeliveryAddress = form.watch(fieldConfig.name);
  const immutableProvinceCode = form.watch("_hiddenValues.immutableProvinceCode") as string | undefined;

  const homeDeliveryAddressWithOverrides = useMemo(() => {
    return applyAddressOverrides(homeDeliveryAddress, {
      provinceCode: immutableProvinceCode,
    });
  }, [applyAddressOverrides, homeDeliveryAddress, immutableProvinceCode]);

  const showShippingAddressSelector = () => {
    setIsAddressDialogOpen(true);
  };

  const hideShippingAddressSelector = () => {
    setIsAddressDialogOpen(false);
  };

  const showNewAddressForm = () => {
    const currentAddress: AddressStored = form.getValues(fieldConfig.name);

    form.setValue(
      fieldConfig.name,
      getEmptyAddress({
        currentAddress,
        overrides: {
          countryCode: fieldConfig.emptyAddress.countryCode!,
          countryName: fieldConfig.emptyAddress.countryName!,
          ...(isAddressFormWithProvince
            ? { regionCode: currentAddress?.regionCode, region: currentAddress?.region }
            : {}),
        },
      })
    );
    setMode("new");
    setShippingAddressOnCartState.reset();

    if (isAddressDialogOpen) {
      hideShippingAddressSelector();
    }
  };

  const buildAddressWithContact = useCallback(
    (address: AddressStored): Address => {
      const magentoAddress = getMagentoAddress({
        methodType: "atHome",
        address,
        countryCode: config.ISO_COUNTRY_CODE,
      });

      if (!magentoAddress) {
        throw new Error("Failed to build Magento address from home delivery address");
      }

      return {
        ...magentoAddress,
        firstname,
        lastname,
        telephone,
        ...(isAddressFormWithProvince && immutableProvinceCode ? { regionCode: immutableProvinceCode } : {}),
      };
    },
    [config.ISO_COUNTRY_CODE, firstname, immutableProvinceCode, isAddressFormWithProvince, lastname, telephone]
  );

  const setNewShippingAddressOnCart = async ({
    address,
    saveToProfile = false,
  }: {
    address: AddressStored;
    saveToProfile?: boolean;
  }) => {
    const addressWithContact: Address = buildAddressWithContact(address);

    await setShippingAddressOnCart(addressWithContact);

    if (saveToProfile) {
      saveAddressInProfileMutation(addressWithContact);
    }
  };

  const onAddressSelect = async (address: AddressStored) => {
    form.setValue(fieldConfig.name, address);

    setMode("view");
    hideShippingAddressSelector();

    if (address.id === homeDeliveryAddress.id) {
      return;
    }

    try {
      await setNewShippingAddressOnCart({ address });
    } catch (error) {
      newrelic.noticeError(
        error instanceof Error
          ? error
          : new Error("AddressHomeDeliveryField: Failed to set shipping address on address select")
      );
    }
  };

  const validateNewAddress = async () => {
    const cartId = getCustomerCartIdQuery.data?.customerCart.id;

    if (!cartId) {
      newrelic.noticeError("No cart ID available when trying to set shipping address.");
      return false;
    }

    const address = form.getValues(fieldConfig.name);
    const addressFieldKeys = Object.keys(address).map((key) => `${fieldConfig.name}.${key}`);

    const isAddressValid = await form.trigger(addressFieldKeys, { shouldFocus: true });

    return isAddressValid;
  };

  const saveNewAddress = async (addressOverride?: AddressStored) => {
    const isAddressValid = addressOverride ? true : await validateNewAddress();

    if (!isAddressValid) {
      return false;
    }

    const address = addressOverride ?? form.getValues(fieldConfig.name);

    address.id = address.id || `temp-id-${Math.random()}`;
    address.firstname = address.firstname || firstname;
    address.lastname = address.lastname || lastname;
    address.telephone = address.telephone || telephone;

    form.setValue(fieldConfig.name, address);

    await setNewShippingAddressOnCart({ address, saveToProfile: true });

    setMode("view");

    return true;
  };

  useEffect(() => {
    switch (saveAddressInProfileMutationResult.status) {
      case "fulfilled":
        toast.success(__("cart.checkout.shipping_address.edit_dialog.create_new.success"));
        break;
      case "rejected":
        toast.error(__("cart.checkout.shipping_address.edit_dialog.create_new.error"));
        break;
      default:
        break;
    }
  }, [saveAddressInProfileMutationResult.status, __]);

  useEffect(function setShippingAddressOnCartOnInitialMount() {
    if (mode === "new") {
      return;
    }

    const address = hasAddressHomeDeliveryValue(homeDeliveryAddress)
      ? homeDeliveryAddress
      : addressHomeDeliveryFieldFactory.getDefaultValue({ fieldConfig });

    form.setValue(fieldConfig.name, address);

    setNewShippingAddressOnCart({ address }).catch((error: unknown) => {
      newrelic.noticeError(
        error instanceof Error
          ? error
          : new Error("AddressHomeDeliveryField: Failed to set shipping address on cart on initial mount")
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shippingAddressErrorMessage = getShippingAddressErrorMessage(setShippingAddressOnCartState.error, __);

  return {
    mode,
    isFormMode: mode === "new",
    homeDeliveryAddress,
    homeDeliveryAddressWithOverrides,
    immutableProvinceCode: isAddressFormWithProvince ? immutableProvinceCode : undefined,
    shippingAddressErrorMessage,
    isAddressDialogOpen,
    isSaveAddressPending:
      setShippingAddressOnCartState.isLoading || getCustomerCartIdQuery.isLoading || countryQueryState.isLoading,
    isOnDialogSubmitLoading: setShippingAddressOnCartState.isLoading,
    showShippingAddressSelector,
    hideShippingAddressSelector,
    showNewAddressForm,
    onAddressSelect,
    validateNewAddress,
    saveNewAddress,
  };
};
